import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { getCourseByPath } from '../services/courseService';
import { resolveMediaUrl } from '../utils/media';
import { loadRazorpayScript, createPaymentOrder, verifyPayment, checkCourseEnrollment } from '../services/paymentService';
import './CourseDetail.css';

// Smart parser for Course Description to handle literal \n, Tech Stack blocks & Outcomes
const renderCourseDescription = (rawDescription) => {
  if (!rawDescription) return null;

  const normalized = rawDescription
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();

  const lines = normalized
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  const elements = [];
  let currentTechStack = null;
  let currentChecklist = null;

  const flushTechStack = () => {
    if (currentTechStack && currentTechStack.items.length > 0) {
      elements.push(
        <div className="cd-tech-stack-card" key={`tech-stack-${elements.length}`}>
          <div className="cd-tech-stack-header">
            <span className="cd-tech-stack-icon">⚡</span>
            <h4>{currentTechStack.title}</h4>
          </div>
          <div className="cd-tech-stack-grid">
            {currentTechStack.items.map((item, idx) => (
              <div className="cd-tech-stack-row" key={idx}>
                <span className="cd-tech-label">{item.label}</span>
                <div className="cd-tech-pills">
                  {item.values.map((v, vi) => (
                    <span className="cd-tech-pill" key={vi}>{v}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
      currentTechStack = null;
    }
  };

  const flushChecklist = () => {
    if (currentChecklist && currentChecklist.items.length > 0) {
      elements.push(
        <div className="cd-outcomes-card" key={`checklist-${elements.length}`}>
          {currentChecklist.title && (
            <h4 className="cd-outcomes-title">{currentChecklist.title}</h4>
          )}
          <div className="cd-outcomes-grid">
            {currentChecklist.items.map((item, idx) => (
              <div className="cd-outcome-item" key={idx}>
                <span className="cd-outcome-check">✔</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );
      currentChecklist = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (/^tech\s+stack\s*(covered)?\s*:?$/i.test(line)) {
      flushChecklist();
      flushTechStack();
      currentTechStack = { title: line.replace(/:$/, ''), items: [] };
      continue;
    }

    const techMatch = line.match(/^([^:]+):\s*(.+)$/);
    if (currentTechStack && techMatch) {
      const label = techMatch[1].trim();
      const rawValues = techMatch[2].trim();
      const values = rawValues
        .split(/,\s*(?:or\s+)?|\s+or\s+/i)
        .map((v) => v.replace(/^or\s+/i, '').trim())
        .filter(Boolean);
      currentTechStack.items.push({ label, values });
      continue;
    } else if (currentTechStack) {
      flushTechStack();
    }

    if (
      /^(by the end of (the|this) course|learners will be able to|what you will get|key takeaways)\b/i.test(line) &&
      line.endsWith(':')
    ) {
      flushChecklist();
      flushTechStack();
      currentChecklist = { title: line, items: [] };
      continue;
    }

    if (currentChecklist) {
      const isBulletOrAction =
        /^[•\-*✓✔]\s*/.test(line) ||
        /^(Build|Work|Implement|Develop|Write|Deploy|Handle|Master|Create|Design|Understand|Learn)\b/i.test(line);

      if (isBulletOrAction) {
        currentChecklist.items.push(line.replace(/^[•\-*✓✔]\s*/, '').trim());
        continue;
      } else {
        flushChecklist();
      }
    }

    elements.push(
      <p className="cd-desc-paragraph" key={`p-${elements.length}`}>
        {line}
      </p>
    );
  }

  flushChecklist();
  flushTechStack();

  return elements;
};

function CourseDetail() {
  const { path } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const initialMode = searchParams.get('mode') === 'offline' ? 'offline' : 'online';
  const [selectedMode, setSelectedMode] = useState(initialMode);
  const [showTcModal, setShowTcModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);

  // Payment & Checkout State
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [studentDetails, setStudentDetails] = useState({
    name: user?.name || user?.username || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [confirmedEnrollment, setConfirmedEnrollment] = useState(null);

  // Active Enrollment Status State
  const [enrollmentStatus, setEnrollmentStatus] = useState({
    isEnrolled: false,
    isExpired: false,
    enrollment: null,
    loading: true
  });

  // Check if current logged-in user is already actively enrolled in this course
  useEffect(() => {
    let isMounted = true;
    const fetchEnrollmentStatus = async () => {
      if (!user || !course?._id) {
        if (isMounted) setEnrollmentStatus({ isEnrolled: false, isExpired: false, enrollment: null, loading: false });
        return;
      }
      try {
        const res = await checkCourseEnrollment(course._id);
        if (isMounted && res.success) {
          setEnrollmentStatus({
            isEnrolled: Boolean(res.isEnrolled),
            isExpired: Boolean(res.isExpired),
            enrollment: res.enrollment || null,
            loading: false
          });
        }
      } catch (err) {
        if (isMounted) setEnrollmentStatus({ isEnrolled: false, isExpired: false, enrollment: null, loading: false });
      }
    };

    fetchEnrollmentStatus();
    return () => { isMounted = false; };
  }, [user, course?._id]);

  // Synchronize student details when user session is loaded
  useEffect(() => {
    if (user) {
      setStudentDetails((prev) => ({
        name: prev.name || user.name || user.username || '',
        email: prev.email || user.email || '',
        phone: prev.phone || user.phone || ''
      }));
    }
  }, [user]);

  // Accordion open/close state
  const [openModules, setOpenModules] = useState({ 0: true, 1: true });
  const [openFaqs, setOpenFaqs] = useState({ 0: true });

  useEffect(() => {
    if (searchParams.get('mode') === 'offline') {
      setSelectedMode('offline');
    }
  }, [searchParams]);

  useEffect(() => {
    if (path) fetchCourse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCourseByPath(path);
      if (response.success && response.data && response.data.isActive !== false) {
        setCourse(response.data);
      } else {
        setError('Course not found or currently unavailable');
      }
    } catch (err) {
      console.error('Error fetching course:', err);
      setError(err.message || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);

  const getCourseImage = (c) => resolveMediaUrl(c?.image) || '/backend_dev_image.jpeg';

  const getDiscount = (price, originalPrice) => {
    if (!originalPrice || originalPrice <= price) return null;
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  const toggleModule = (idx) => {
    setOpenModules(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleAllModules = (itemsCount) => {
    const allOpen = Object.keys(openModules).length >= itemsCount && Object.values(openModules).every(Boolean);
    if (allOpen) {
      setOpenModules({});
    } else {
      const next = {};
      for (let i = 0; i < itemsCount; i++) next[i] = true;
      setOpenModules(next);
    }
  };

  const toggleFaq = (idx) => {
    setOpenFaqs(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Payment Checkout Handlers
  const handleOpenCheckout = () => {
    if (!user) {
      const currentCoursePath = course?.path || path;
      const modeParam = selectedMode === 'offline' ? '?mode=offline' : '';
      navigate(`/login?redirect=${encodeURIComponent(`/courses/${currentCoursePath}${modeParam}`)}`);
      return;
    }

    // If already actively enrolled in this course, take student straight to My Courses dashboard
    if (enrollmentStatus.isEnrolled && !enrollmentStatus.isExpired) {
      navigate('/my-courses');
      return;
    }

    setPaymentError(null);
    setShowCheckoutModal(true);
  };

  const handleProcessPayment = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!studentDetails.name.trim() || !studentDetails.email.trim() || !studentDetails.phone.trim()) {
      setPaymentError('Please fill in your Name, Email, and Phone number.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(studentDetails.email.trim())) {
      setPaymentError('Please enter a valid email address.');
      return;
    }

    const phoneDigits = (studentDetails.phone || '').trim().replace(/\D/g, '');
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(phoneDigits)) {
      setPaymentError('Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.');
      return;
    }

    try {
      setPaymentProcessing(true);
      setPaymentError(null);

      // 1. Create order securely on backend
      const orderRes = await createPaymentOrder({
        courseId: course._id,
        mode: selectedMode,
        studentName: studentDetails.name.trim(),
        studentEmail: studentDetails.email.trim(),
        studentPhone: phoneDigits
      });

      if (!orderRes.success) {
        setPaymentError(orderRes.message || 'Failed to create payment order');
        setPaymentProcessing(false);
        return;
      }

      // 2. Load Razorpay script
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        setPaymentError('Could not load Razorpay secure checkout. Please check your internet connection.');
        setPaymentProcessing(false);
        return;
      }

      // 3. Open Razorpay Checkout Modal
      const options = {
        key: orderRes.key,
        amount: orderRes.amount,
        currency: orderRes.currency,
        name: 'DGPL Courses • Durbhasi Gurukulam',
        description: selectedMode === 'offline'
          ? `${course.title} (Classroom Batch - Jaipur)`
          : `${course.title} (Live Online Cohort)`,
        order_id: orderRes.orderId,
        prefill: {
          name: studentDetails.name.trim(),
          email: studentDetails.email.trim(),
          contact: phoneDigits
        },
        notes: {
          courseId: course._id,
          mode: selectedMode
        },
        theme: {
          color: '#2563eb'
        },
        handler: async function (response) {
          try {
            setPaymentProcessing(true);
            const verifyRes = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              courseId: course._id,
              mode: selectedMode,
              studentName: studentDetails.name.trim(),
              studentEmail: studentDetails.email.trim(),
              studentPhone: phoneDigits
            });

            if (verifyRes.success) {
              setConfirmedEnrollment(verifyRes.enrollment);
              setEnrollmentStatus({
                isEnrolled: true,
                isExpired: false,
                enrollment: verifyRes.enrollment,
                loading: false
              });
              setShowCheckoutModal(false);
              setShowSuccessModal(true);
            } else {
              setPaymentError(verifyRes.message || 'Payment verification failed');
            }
          } catch (verifyErr) {
            console.error('Verification error:', verifyErr);
            setPaymentError(verifyErr.message || 'Error occurred during payment verification');
          } finally {
            setPaymentProcessing(false);
          }
        },
        modal: {
          ondismiss: function () {
            setPaymentProcessing(false);
          }
        }
      };

      const rzpInstance = new window.Razorpay(options);
      rzpInstance.open();
    } catch (err) {
      console.error('Payment checkout error:', err);
      setPaymentError(err.message || 'Failed to start payment process');
      setPaymentProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="cd-page-container">
        <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
          <div className="spinner" style={{ width: '44px', height: '44px', border: '3px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px auto' }}></div>
          <p style={{ color: 'var(--text-muted)' }}>Loading course details, curriculum, and enrollment options...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="cd-page-container">
        <div className="container" style={{ padding: '80px 0', textAlign: 'center' }}>
          <h2>Course Not Found</h2>
          <p style={{ color: 'var(--text-muted)', margin: '12px 0 24px 0' }}>{error || 'The requested engineering cohort could not be found.'}</p>
          <button className="btn btn-primary btn-md" onClick={() => navigate('/courses')}>
            ← Back to All Courses
          </button>
        </div>
      </div>
    );
  }

  const discount = getDiscount(course.price, course.originalPrice);
  const courseImage = getCourseImage(course);
  const isOfflineAvailable = Boolean(course.isOfflineAvailable);

  const offlinePrice = course.offlinePrice || 10000;
  const offlineOriginalPrice = course.offlineOriginalPrice || (course.offlinePrice ? course.offlinePrice : 15000);
  const offlineDiscount = offlineOriginalPrice > offlinePrice
    ? Math.round(((offlineOriginalPrice - offlinePrice) / offlineOriginalPrice) * 100)
    : 0;

  // Check if logged-in student has an approved custom price for this course (Online mode only)
  const userCustomOnline = (user && Array.isArray(user.customPricing))
    ? user.customPricing.find((p) => {
        const cid = typeof p.courseId === 'object' && p.courseId !== null ? p.courseId._id : p.courseId;
        return cid === course._id;
      })
    : null;

  const hasCustomOnlineConcession = Boolean(
    selectedMode === 'online' &&
    userCustomOnline &&
    typeof userCustomOnline.customPrice === 'number' &&
    userCustomOnline.customPrice >= 0
  );

  let displayPrice = course.price;
  let displayOriginalPrice = course.originalPrice;
  let displayDiscountLabel = discount ? `UPTO ${discount}% OFF` : null;

  if (isOfflineAvailable && selectedMode === 'offline') {
    displayPrice = offlinePrice;
    displayOriginalPrice = offlineOriginalPrice;
    displayDiscountLabel = offlineDiscount > 0 ? `UPTO ${offlineDiscount}% OFF` : null;
  } else if (hasCustomOnlineConcession) {
    displayPrice = userCustomOnline.customPrice;
    displayOriginalPrice = course.price;
    const customPercent = course.price > displayPrice
      ? Math.round(((course.price - displayPrice) / course.price) * 100)
      : null;
    displayDiscountLabel = customPercent ? `SCHOLARSHIP ${customPercent}% OFF` : 'SPECIAL CONCESSION';
  }

  const cleanShortDesc = (course.shortDescription || '')
    .replace(/\\r\\n/g, ' ')
    .replace(/\\n/g, ' ')
    .trim();

  const curriculumItems = course.whatYouWillLearn?.length > 0
    ? course.whatYouWillLearn
    : [
        'Systems Architecture & High-Concurrency Patterns',
        'Memory Safety, Concurrency, and Low-Latency IO',
        'Production Database Sharding, Replication & Failover',
        'Docker Containerization, Kubernetes Clustering & GitOps',
        'Capstone Architecture Defense & Live Review'
      ];

  const handleStartLearning = () => {
    const el = document.getElementById('enroll-sidebar');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="cd-page-container">
      <Helmet>
        <title>{`${course.title} | Durbhasi Gurukulam`}</title>
        <meta name="description" content={cleanShortDesc || `${course.title} cohort at Durbhasi Gurukulam`} />
      </Helmet>

      {/* Breadcrumb Navigation */}
      <div className="container">
        <nav className="course-breadcrumb-nav" aria-label="Breadcrumb">
          <ol className="course-breadcrumb">
            <li><Link to="/">Home</Link></li>
            <li className="course-breadcrumb-sep" aria-hidden="true">&rsaquo;</li>
            <li><Link to="/courses">Courses</Link></li>
            <li className="course-breadcrumb-sep" aria-hidden="true">&rsaquo;</li>
            <li className="course-breadcrumb-current">{course.title}</li>
          </ol>
        </nav>

        {/* 2-Column Course Details Layout */}
        <div className="detail-content-layout">
          {/* Left Column: Course Header & Main Content */}
          <div className="detail-main-col">
            {/* Technical Blueprint Hero Container */}
            <header className="detail-course-hero" id="course-hero">
              <div className="hero-top-row">
                <div className="hero-tech-icon" aria-label="Course Technology">
                  <span>{course.title.charAt(0)}</span>
                </div>
                <div className="hero-top-badges">
                  <div className="hero-badge-cert">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="8" r="6"></circle>
                      <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"></path>
                    </svg>
                    <span>Certification Available</span>
                  </div>
                  <div className="hero-badge-rating">
                    <span className="hero-rating-star">★</span>
                    <span>{course.rating || 4.9} (Verified Reviews)</span>
                  </div>
                </div>
              </div>

              <h1 className="hero-course-title">{course.title}</h1>

              {cleanShortDesc && (
                <p className="hero-course-subtitle">{cleanShortDesc}</p>
              )}

              {/* Course Metadata Strip */}
              <div className="hero-meta-row">
                <span className="hero-meta-tag">⏱️ {course.duration || '8 Weeks'}</span>
                <span className="hero-meta-tag">📊 {course.level || 'Intermediate'}</span>
                <span className="hero-meta-tag">👨‍🏫 Lead: {course.instructor || 'Senior Faculty'}</span>
                <span className="hero-meta-tag">🏷️ {course.category?.name || 'Systems'}</span>
              </div>

              <div className="hero-bottom-cta">
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  onClick={handleStartLearning}
                >
                  Start Learning &rarr;
                </button>
              </div>
            </header>

            {/* 1. Course Overview & Engineering Philosophy */}
            <section className="detail-content-section" id="overview">
              <div className="detail-section-header">
                <h2 className="detail-section-title">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  Course Overview &amp; Engineering Philosophy
                </h2>
              </div>
              <div className="detail-overview-prose">
                {renderCourseDescription(course.description)}
              </div>
            </section>

            {/* 2. What You Will Learn & Master */}
            {course.whatYouWillLearn && course.whatYouWillLearn.length > 0 && (
              <section className="detail-content-section" id="outcomes">
                <div className="detail-section-header">
                  <h2 className="detail-section-title">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                    What You Will Learn &amp; Master
                  </h2>
                  <p className="detail-section-desc">
                    Concrete engineering competencies acquired through direct implementations, benchmarks, and pull requests.
                  </p>
                </div>
                <div className="outcomes-cards-grid">
                  {course.whatYouWillLearn.map((item, idx) => (
                    <div className="outcome-card-item" key={idx}>
                      <span className="outcome-check-icon">✓</span>
                      <span className="outcome-card-text">{item}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 3. Comprehensive Curriculum & Modules */}
            <section className="detail-content-section" id="curriculum">
              <div className="curriculum-header-row">
                <div>
                  <h2 className="detail-section-title">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    Comprehensive Curriculum &amp; Modules
                  </h2>
                  <p className="detail-section-desc">
                    Structured progression with hands-on milestone code reviews and capstone defense.
                  </p>
                </div>
                <button
                  type="button"
                  className="curriculum-toggle-all-btn"
                  onClick={() => toggleAllModules(curriculumItems.length)}
                >
                  Toggle All Modules
                </button>
              </div>

              <div className="modules-accordion-list">
                {curriculumItems.map((mod, idx) => (
                  <div className={`module-accordion-item ${openModules[idx] ? 'open' : ''}`} key={idx}>
                    <div
                      className="module-item-header"
                      onClick={() => toggleModule(idx)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleModule(idx); }}
                    >
                      <div className="module-title-box">
                        <span className="module-num-badge">Module {String(idx + 1).padStart(2, '0')}</span>
                        <span className="module-title-text">{mod}</span>
                      </div>
                      <span className="module-chevron">{openModules[idx] ? '▲' : '▼'}</span>
                    </div>
                    {openModules[idx] && (
                      <div className="module-item-body">
                        <p className="module-body-desc">
                          In-depth architectural lecture, interactive coding labs, and private GitHub Pull Request review for this milestone.
                        </p>
                        <div className="module-deliverables">
                          <span className="deliverable-tag">✓ Hands-on Lab Implementation</span>
                          <span className="deliverable-tag">✓ Private PR Code Review</span>
                          <span className="deliverable-tag">✓ Faculty Office Hours Q&amp;A</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* 4. Technologies & Tools Covered */}
            {course.tags && course.tags.length > 0 && (
              <section className="detail-content-section" id="technologies">
                <div className="detail-section-header">
                  <h2 className="detail-section-title">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="16 18 22 12 16 6"></polyline>
                      <polyline points="8 6 2 12 8 18"></polyline>
                    </svg>
                    Technologies, Frameworks &amp; Diagnostic Tools
                  </h2>
                  <p className="detail-section-desc">
                    You will write and test code using industry-standard production runtimes and performance tools.
                  </p>
                </div>
                <div className="tech-tools-grid">
                  {course.tags.flatMap(t => t.split(',')).map((tag, idx) => (
                    tag.trim() && (
                      <div className="tech-tool-item" key={idx}>
                        <span className="tech-tool-dot">⚡</span>
                        <span className="tech-tool-name">{tag.trim()}</span>
                      </div>
                    )
                  ))}
                </div>
              </section>
            )}

            {/* 5. Production Capstone Project Showcase */}
            <section className="detail-content-section" id="capstone">
              <div className="detail-section-header">
                <h2 className="detail-section-title">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                    <line x1="8" y1="21" x2="16" y2="21"></line>
                    <line x1="12" y1="17" x2="12" y2="21"></line>
                  </svg>
                  Production Capstone Project
                </h2>
                <p className="detail-section-desc">
                  No toy tutorials or throwaway exercises. You will build a complete system designed for real load.
                </p>
              </div>
              <div className="capstone-showcase-card">
                <h3 className="capstone-title">End-to-End {course.title} Production Architecture</h3>
                <p className="capstone-desc">
                  Design, implement, profile, and defend a complete production-grade deployment. Implement metrics instrumentation, resilience recovery models, automated testing suites, and containerized deployment pipelines.
                </p>
                <div className="capstone-milestone-pills">
                  <span>Milestone 1: Low-Level Core</span>
                  <span>Milestone 2: Concurrency &amp; IO</span>
                  <span>Milestone 3: Clustering &amp; Scaling</span>
                  <span>Final: Live Technical Defense</span>
                </div>
              </div>
            </section>

            {/* 6. Target Audience & Prerequisites */}
            <section className="detail-content-section" id="audience">
              <div className="detail-section-header">
                <h2 className="detail-section-title">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  Target Audience &amp; Prerequisites
                </h2>
              </div>
              <div className="split-info-grid">
                <div className="split-info-card">
                  <h3 className="split-card-title">Who This Course Is For</h3>
                  <ul className="split-list">
                    <li>Engineers seeking to transition into senior backend or systems roles.</li>
                    <li>Developers wanting production-grade architectural pull request feedback.</li>
                    <li>Technical professionals who want to master real concurrency, scalability, and performance.</li>
                  </ul>
                </div>

                <div className="split-info-card">
                  <h3 className="split-card-title">Prerequisites &amp; Requirements</h3>
                  <ul className="split-list">
                    {course.requirements && course.requirements.length > 0 ? (
                      course.requirements.map((req, i) => <li key={i}>{req}</li>)
                    ) : (
                      <>
                        <li>Working knowledge of at least one programming language (Python, Java, C++, Go, or Rust).</li>
                        <li>Basic familiarity with Linux command line and Git version control.</li>
                        <li>8 to 12 hours of weekly commitment for interactive sessions and lab implementations.</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </section>

            {/* 7. Lead Faculty Profile */}
            <section className="detail-content-section" id="faculty">
              <div className="detail-section-header">
                <h2 className="detail-section-title">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  Course Instructor &amp; Lead Architect
                </h2>
              </div>
              <div className="faculty-profile-card">
                <div className="faculty-avatar-box">
                  {course.instructor?.charAt(0) || 'F'}
                </div>
                <div className="faculty-details-box">
                  <h3 className="faculty-name">{course.instructor}</h3>
                  <div className="faculty-role">Senior Systems Faculty • Durbhasi Gurukulam</div>
                  <p className="faculty-bio">
                    Extensive experience architecting resilient backend microservices, Linux infrastructure, and high-throughput systems. Dedicated to mentor-led code reviews and practical engineering mastery.
                  </p>
                </div>
              </div>
            </section>

            {/* 8. Verified Certificate of Mastery */}
            <section className="detail-content-section" id="certificate">
              <div className="detail-section-header">
                <h2 className="detail-section-title">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="8" r="7"></circle>
                    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                  </svg>
                  Verified Credential &amp; Certificate of Mastery
                </h2>
                <p className="detail-section-desc">
                  Issued exclusively upon passing all milestone code reviews and defending your capstone architecture live.
                </p>
              </div>
              <div className="certificate-showcase-card">
                <div className="cert-emblem-box">
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                  <span className="cert-emblem-text">GURUKULAM</span>
                </div>
                <div className="cert-details-box">
                  <h3 className="cert-title">Verified Certificate in Systems &amp; Software Engineering</h3>
                  <p className="cert-desc">
                    Unlike passive video certificates, this credential certifies that the recipient has written and defended real production-grade code evaluated by practicing architects.
                  </p>
                  <div className="cert-meta-row">
                    <span className="cert-meta-item">✓ Verifiable Digital ID</span>
                    <span className="cert-meta-item">✓ LinkedIn Credential</span>
                    <span className="cert-meta-item">✓ Public GitHub Defense Record</span>
                  </div>
                </div>
              </div>
            </section>

            {/* 9. Course FAQs */}
            <section className="detail-content-section" id="faqs">
              <div className="detail-section-header">
                <h2 className="detail-section-title">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  Frequently Asked Questions
                </h2>
              </div>
              <div className="course-faq-list">
                {[
                  {
                    q: 'What happens if I miss a live cohort session?',
                    a: 'All interactive sessions are recorded in HD and published within 2 hours to the Student Portal, complete with repository notes and architectural diagrams. You retain lifetime access.'
                  },
                  {
                    q: 'How do the 1-on-1 GitHub Pull Request reviews work?',
                    a: 'For each module milestone, you submit your solution as a private pull request. Your faculty mentor performs a thorough line-by-line review evaluating correctness, thread safety, memory efficiency, and idiomatic patterns.'
                  },
                  {
                    q: 'Can I join in-person at the Jaipur campus?',
                    a: 'Yes, offline classroom batches are held at our Jaipur campus (P.No. 18, Radha Vihar, Manchwa, Jaipur). You get in-person practical lab access, face-to-face mentorship, and UPTO 33% counseling fee concessions.'
                  }
                ].map((item, idx) => (
                  <div className={`course-faq-item ${openFaqs[idx] ? 'open' : ''}`} key={idx}>
                    <div className="course-faq-question" onClick={() => toggleFaq(idx)}>
                      <span>{item.q}</span>
                      <span>{openFaqs[idx] ? '▲' : '▼'}</span>
                    </div>
                    {openFaqs[idx] && (
                      <div className="course-faq-answer">
                        {item.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column: Permanently Sticky Enrollment Card & Certificate Preview */}
          <div className="detail-sidebar-col" id="enroll-sidebar">
            <aside className="detail-enroll-card" aria-label="Course Enrollment Card">
              <div className="detail-enroll-thumb">
                <img
                  src={courseImage}
                  alt={course.title}
                  className="detail-enroll-img"
                  onError={(e) => { e.currentTarget.src = '/backend_dev_image.jpeg'; }}
                />
                <span className="course-badge badge-live detail-thumb-badge">
                  {isOfflineAvailable && selectedMode === 'offline' ? 'Classroom Cohort' : 'Live Cohort'}
                </span>
              </div>

              <div className="detail-enroll-body">
                {/* Learning Mode Switcher */}
                {isOfflineAvailable && (
                  <div className="cd-mode-container">
                    <div className="cd-mode-header">
                      <span className="cd-mode-heading">Learning Mode</span>
                      {selectedMode === 'offline' && (
                        <span className="cd-mode-badge-offline">Classroom Batch</span>
                      )}
                    </div>
                    <div className="cd-mode-toggle-group">
                      <button
                        type="button"
                        className={`cd-mode-btn ${selectedMode === 'online' ? 'active' : ''}`}
                        onClick={() => setSelectedMode('online')}
                      >
                        🌐 Online
                      </button>
                      <button
                        type="button"
                        className={`cd-mode-btn ${selectedMode === 'offline' ? 'active' : ''}`}
                        onClick={() => setSelectedMode('offline')}
                      >
                        🏢 Offline
                      </button>
                    </div>

                    {selectedMode === 'offline' && (
                      <div className="cd-offline-info-container">
                        <div className="cd-venue-card">
                          <div className="cd-venue-header">
                            <span className="cd-venue-icon">📍</span>
                            <span className="cd-venue-title">Classroom Venue (Jaipur Campus)</span>
                          </div>
                          <p className="cd-venue-address">
                            P.No. 18, Radha Vihar, Manchwa, Jaipur, Rajasthan 303706
                          </p>
                          <div className="cd-venue-badges">
                            <span>🏢 In-Person Practical Labs</span>
                            <span>👨‍🏫 Direct Mentor Mentorship</span>
                          </div>
                        </div>

                        <div className="cd-helpline-card">
                          <div className="cd-helpline-header">
                            <span className="cd-helpline-title">Admissions &amp; Concession Desk</span>
                            <button
                              type="button"
                              className="cd-tc-pill-btn"
                              onClick={() => setShowTcModal(true)}
                            >
                              Details ℹ️
                            </button>
                          </div>
                          <p className="cd-helpline-desc">
                            Call or message our counselors to evaluate your profile for <strong>{offlineDiscount > 0 ? `UPTO ${offlineDiscount}% fee concession` : 'fee concession'}</strong>.
                          </p>
                          <div className="cd-helpline-actions">
                            <a href="tel:+917852034945" className="btn btn-secondary btn-sm" style={{ width: '100%', justifyContent: 'center' }}>
                              📞 Call: +91 7852034945
                            </a>
                            <a
                              href={`https://wa.me/917852034945?text=${encodeURIComponent(`Hi Durbhasi Gurukulam, I am interested in the Offline Classroom Batch for ${course.title} and want to check my eligibility for ${offlineDiscount > 0 ? `the UPTO ${offlineDiscount}%` : 'the'} discount.`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-primary btn-sm"
                              style={{ width: '100%', justifyContent: 'center' }}
                            >
                              💬 Chat on WhatsApp
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Price Display */}
                <div className="detail-price-row">
                  <span className="detail-current-price">{formatPrice(displayPrice)}</span>
                  {displayOriginalPrice && displayOriginalPrice > displayPrice && (
                    <span className="detail-original-price">{formatPrice(displayOriginalPrice)}</span>
                  )}
                  {displayDiscountLabel && (
                    <span className="detail-discount-pill">{displayDiscountLabel}</span>
                  )}
                </div>

                <div className="detail-seats-warning">
                  <span className="detail-seats-dot"></span>
                  <span>Limited cohort: 30 developers maximum</span>
                </div>

                {/* Active Enrollment Info Badge */}
                {enrollmentStatus.isEnrolled && !enrollmentStatus.isExpired && (
                  <div style={{
                    background: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    borderRadius: '8px',
                    padding: '12px 14px',
                    marginBottom: '14px',
                    color: '#065f46',
                    textAlign: 'left'
                  }}>
                    <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem' }}>
                      <span>🎉</span> Active Cohort Enrollment
                    </div>
                    <div style={{ fontSize: '0.82rem', marginTop: '4px', color: '#047857' }}>
                      Access: <strong>{enrollmentStatus.enrollment?.validityDuration || course.duration}</strong>
                      {enrollmentStatus.enrollment?.validUntil && (
                        <div>
                          Valid till: <strong>{new Date(enrollmentStatus.enrollment.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Enroll / Apply CTA */}
                <button
                  type="button"
                  className={`btn btn-lg detail-apply-btn ${enrollmentStatus.isEnrolled && !enrollmentStatus.isExpired ? 'btn-success' : 'btn-primary'}`}
                  style={enrollmentStatus.isEnrolled && !enrollmentStatus.isExpired ? { background: '#10b981', borderColor: '#059669', color: '#ffffff' } : {}}
                  onClick={handleOpenCheckout}
                >
                  {!user
                    ? 'Login to Enroll in Cohort →'
                    : enrollmentStatus.isEnrolled && !enrollmentStatus.isExpired
                    ? '✅ Already Enrolled • Go to My Courses →'
                    : enrollmentStatus.isEnrolled && enrollmentStatus.isExpired
                    ? '⚠️ Access Expired • Renew Enrollment →'
                    : isOfflineAvailable && selectedMode === 'offline'
                    ? 'Enroll in Jaipur Batch →'
                    : 'Enroll in Live Cohort →'}
                </button>

                {course.syllabusDownloadLink && (
                  <a
                    href={resolveMediaUrl(course.syllabusDownloadLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary btn-sm"
                    style={{ marginTop: '10px', width: '100%', justifyContent: 'center' }}
                  >
                    📄 Download Detailed Syllabus
                  </a>
                )}
              </div>
            </aside>

            {/* Certificate of Completion Showcase */}
            <section className="sidebar-cert-card" aria-label="Certificate of Completion Showcase">
              <div
                className="sidebar-cert-thumb"
                onClick={() => setShowCertModal(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowCertModal(true); }}
                aria-label="Enlarge Certificate of Completion Preview"
              >
                <img
                  src="/durbhasi-gurukulam-certificate-of-completion.png"
                  alt="Durbhasi Gurukulam Certificate of Completion"
                  className="sidebar-cert-img"
                  loading="lazy"
                />
                <div className="sidebar-cert-thumb-overlay">
                  <span>🔍 Enlarge Preview</span>
                </div>
              </div>
              <div className="sidebar-cert-body">
                <div className="sidebar-cert-header">
                  <h3 className="sidebar-cert-title">Certificate of Completion</h3>
                </div>
                <p className="sidebar-cert-desc">
                  Students receive an official verifiable credential upon completing the cohort and defending their capstone architecture.
                </p>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => setShowCertModal(true)}
                >
                  View Full Certificate
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Bottom CTA Banner */}
      <section className="course-bottom-cta-banner">
        <div className="container">
          <div className="bottom-cta-flex">
            <div>
              <h2 className="bottom-cta-title">Ready to master {course.title}?</h2>
              <p className="bottom-cta-subtitle">
                Limited cohort size of 30 developers. Mentor-led architecture reviews &amp; live sessions.
              </p>
            </div>
            <button
              type="button"
              className={`btn btn-lg ${enrollmentStatus.isEnrolled && !enrollmentStatus.isExpired ? 'btn-success' : 'btn-primary'}`}
              style={enrollmentStatus.isEnrolled && !enrollmentStatus.isExpired ? { background: '#10b981', borderColor: '#059669', color: '#ffffff' } : {}}
              onClick={handleOpenCheckout}
            >
              {!user
                ? 'Login to Enroll in Cohort →'
                : enrollmentStatus.isEnrolled && !enrollmentStatus.isExpired
                ? '✅ Enrolled • Go to My Courses →'
                : enrollmentStatus.isEnrolled && enrollmentStatus.isExpired
                ? 'Renew Enrollment →'
                : 'Enroll Now →'}
            </button>
          </div>
        </div>
      </section>

      {/* Certificate Modal */}
      {showCertModal && (
        <div className="tc-modal-backdrop" onClick={() => setShowCertModal(false)}>
          <div className="tc-modal-card" style={{ maxWidth: '800px', padding: '12px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px' }}>
              <strong style={{ fontSize: '1rem', color: 'var(--text-main)' }}>Official Certificate of Completion</strong>
              <button
                type="button"
                className="tc-modal-close"
                onClick={() => setShowCertModal(false)}
              >
                ✕
              </button>
            </div>
            <img
              src="/durbhasi-gurukulam-certificate-of-completion.png"
              alt="Durbhasi Gurukulam Certificate"
              style={{ width: '100%', borderRadius: 'var(--radius-md)', marginTop: '8px' }}
            />
          </div>
        </div>
      )}

      {/* Offline Batch Inquiry Modal */}
      {showTcModal && (
        <div className="tc-modal-backdrop" onClick={() => setShowTcModal(false)}>
          <div className="tc-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="tc-modal-header">
              <div className="tc-modal-title-group">
                <span className="tc-modal-kicker">Classroom Training Program</span>
                <h3 className="tc-modal-title">Offline Batch &amp; Discount Inquiry</h3>
                <p className="tc-modal-subtitle">
                  {course.title} • Mentor: {course.instructor}
                </p>
              </div>
              <button
                type="button"
                className="tc-modal-close"
                onClick={() => setShowTcModal(false)}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            <div className="tc-modal-body">
              <div className="discount-call-notice">
                <div className="notice-badge">Special Concession: UPTO 33% OFF</div>
                <div className="pricing-summary-row">
                  <span className="concession-price">Starting from ₹10,000</span>
                  <span className="standard-price">Standard Fee: ₹15,000</span>
                </div>
                <p className="notice-desc">
                  A special fee concession of <strong>UPTO 33% (Save up to ₹5,000)</strong> is available for classroom batches. Discount eligibility and final fee approval are evaluated by our admissions desk during counseling based on student profile and seat availability.
                </p>
              </div>

              <div className="venue-info-box">
                <div className="venue-header">
                  <span className="venue-icon">📍</span>
                  <h4 className="venue-title">Classroom Training Venue</h4>
                </div>
                <p className="venue-address">
                  P.No. 18, Radha Vihar, Manchwa, Jaipur, Rajasthan 303706
                </p>
                <div className="venue-meta">
                  <span>🏢 In-Person Practical Labs</span>
                  <span>👨‍🏫 Direct Mentor Mentorship</span>
                </div>
              </div>

              <div className="inquiry-contact-card">
                <div className="contact-heading">Speak to Admissions Desk:</div>
                <p className="contact-subtext">
                  Call or message our counseling team now to verify your discount eligibility and confirm batch timings:
                </p>
                <div className="contact-actions-row">
                  <a href="tel:+917852034945" className="btn btn-secondary btn-sm">
                    📞 Call: +91 7852034945
                  </a>
                  <a
                    href={`https://wa.me/917852034945?text=${encodeURIComponent(`Hi Durbhasi Gurukulam, I am interested in the Offline Classroom Batch for ${course.title} and want to check my eligibility for ${offlineDiscount > 0 ? `the UPTO ${offlineDiscount}%` : 'the'} discount.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm"
                  >
                    💬 Chat on WhatsApp
                  </a>
                </div>
              </div>
            </div>

            <div className="tc-modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowTcModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Razorpay Checkout Modal */}
      {showCheckoutModal && (
        <div className="checkout-backdrop" onClick={() => !paymentProcessing && setShowCheckoutModal(false)}>
          <div className="checkout-card" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-header">
              <div>
                <span className="checkout-kicker">Guaranteed Direct Enrollment</span>
                <h3 className="checkout-title">Confirm Your Cohort Registration</h3>
              </div>
              <button
                type="button"
                className="checkout-close-btn"
                onClick={() => !paymentProcessing && setShowCheckoutModal(false)}
                aria-label="Close checkout modal"
              >
                ✕
              </button>
            </div>

            <div className="checkout-body">
              {paymentError && (
                <div className="checkout-error-banner">
                  ⚠️ {paymentError}
                </div>
              )}

              {/* Course & Mode Summary */}
              <div className="checkout-summary-box">
                <div className="checkout-summary-row">
                  <h4 className="checkout-summary-title">{course.title}</h4>
                  <span className={`checkout-mode-pill ${selectedMode}`}>
                    {selectedMode === 'offline' ? '🏢 Jaipur Classroom' : '🌐 Live Online Cohort'}
                  </span>
                </div>

                {selectedMode === 'offline' && (
                  <div className="checkout-venue-notice">
                    📍 <strong>Venue:</strong> P.No. 18, Radha Vihar, Manchwa, Jaipur, Rajasthan 303706
                  </div>
                )}

                <div className="checkout-price-row">
                  <span className="checkout-price-label">Total Payable Fee (Inclusive of GST)</span>
                  <span className="checkout-price-amount">{formatPrice(displayPrice)}</span>
                </div>
              </div>

              {/* Account Context Notice */}
              {user ? (
                <div style={{ padding: '8px 12px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.85rem', color: '#1e40af' }}>
                  <span>👤 Enrolling as <strong>{user.name || user.username}</strong> ({user.email})</span>
                  <span style={{ fontSize: '0.75rem', background: '#dbeafe', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>Authenticated</span>
                </div>
              ) : (
                <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px', fontSize: '0.82rem', color: '#64748b' }}>
                  💡 Have an account? <Link to="/login" state={{ from: `/courses/${path}` }} style={{ color: '#2563eb', fontWeight: 600 }}>Login first</Link> to link this enrollment directly to your student dashboard.
                </div>
              )}

              {/* Special Online Concession Banner */}
              {hasCustomOnlineConcession && (
                <div style={{ padding: '10px 14px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a', marginBottom: '16px', fontSize: '0.85rem', color: '#92400e' }}>
                  <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>🏷️</span>
                    <span>Approved Online Scholarship Rate: {formatPrice(displayPrice)}</span>
                    <span style={{ fontSize: '0.78rem', textDecoration: 'line-through', color: '#b45309', fontWeight: 500 }}>{formatPrice(course.price)}</span>
                  </div>
                  {userCustomOnline?.reason && (
                    <div style={{ fontSize: '0.78rem', color: '#78350f', marginTop: '3px' }}>
                      Concession Note: {userCustomOnline.reason}
                    </div>
                  )}
                </div>
              )}

              {/* Student Registration Details Form */}
              <div style={{ padding: '8px 12px', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0', fontSize: '0.82rem', color: '#065f46', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>✓</span>
                <span><strong>Enrolling as verified student:</strong> {user?.name || user?.username} ({user?.email})</span>
              </div>

              <form onSubmit={handleProcessPayment} className="checkout-form">
                <div className="checkout-form-group">
                  <label className="checkout-label">
                    Full Name <span>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Rahul Sharma"
                    className="checkout-input"
                    value={studentDetails.name}
                    onChange={(e) => setStudentDetails({ ...studentDetails, name: e.target.value })}
                    disabled={paymentProcessing}
                  />
                </div>

                <div className="checkout-form-group">
                  <label className="checkout-label">
                    Email Address (Account Tied) <span>*</span>
                  </label>
                  <input
                    type="email"
                    required
                    readOnly
                    className="checkout-input"
                    value={studentDetails.email}
                    style={{ background: '#f1f5f9', cursor: 'not-allowed', color: '#334155' }}
                    disabled={paymentProcessing}
                  />
                  <span className="checkout-input-hint">Official receipt and cohort hub access will be permanently linked to this account.</span>
                </div>

                <div className="checkout-form-group">
                  <label className="checkout-label">
                    WhatsApp / Mobile Number <span>*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    inputMode="numeric"
                    pattern="[6-9][0-9]{9}"
                    maxLength={10}
                    placeholder="e.g. 9876543210 (10 digits)"
                    className="checkout-input"
                    value={studentDetails.phone}
                    onChange={(e) => {
                      const cleanPhone = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setStudentDetails({ ...studentDetails, phone: cleanPhone });
                    }}
                    disabled={paymentProcessing}
                  />
                  <span className="checkout-input-hint">Numbers only. Enter valid 10-digit mobile number starting with 6, 7, 8, or 9.</span>
                </div>

                <button
                  type="submit"
                  className="checkout-submit-btn"
                  disabled={paymentProcessing}
                >
                  {paymentProcessing ? (
                    <>
                      <span className="spinner" style={{ width: '18px', height: '18px', borderWidth: '2px', borderTopColor: '#fff', display: 'inline-block' }}></span>
                      Opening Secure Razorpay Gateway...
                    </>
                  ) : (
                    <>
                      🔒 Proceed to Pay {formatPrice(displayPrice)} &rarr;
                    </>
                  )}
                </button>
              </form>

              <div className="checkout-guarantee">
                <span>🛡️ 256-Bit SSL Encrypted</span>
                <span>•</span>
                <span>Official Razorpay Gateway</span>
                <span>•</span>
                <span>Instant Confirmation</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Success & Receipt Modal */}
      {showSuccessModal && confirmedEnrollment && (
        <div className="checkout-backdrop">
          <div className="checkout-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '580px', textAlign: 'center' }}>
            <div className="checkout-body" style={{ padding: '36px 28px' }}>
              <div className="success-check-badge">✓</div>
              <h3 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px 0' }}>
                🎉 Enrollment Successfully Confirmed!
              </h3>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 20px 0' }}>
                Thank you, <strong>{confirmedEnrollment.studentName}</strong>. Your registration for <strong>{confirmedEnrollment.courseTitle}</strong> is active.
              </p>

              {/* Receipt Breakdown */}
              <div className="success-receipt-card">
                <h4>Official Enrollment Receipt</h4>
                <div className="receipt-item-row">
                  <span className="receipt-item-label">Receipt Number:</span>
                  <span className="receipt-item-val" style={{ fontFamily: 'monospace' }}>{confirmedEnrollment.receiptNumber}</span>
                </div>
                <div className="receipt-item-row">
                  <span className="receipt-item-label">Learning Track:</span>
                  <span className="receipt-item-val">
                    {confirmedEnrollment.mode === 'offline' ? 'Classroom Batch (Jaipur)' : 'Live Online Cohort'}
                  </span>
                </div>
                <div className="receipt-item-row">
                  <span className="receipt-item-label">Amount Paid:</span>
                  <span className="receipt-item-val" style={{ color: '#059669' }}>
                    ₹{confirmedEnrollment.amount?.toLocaleString()} ({confirmedEnrollment.currency || 'INR'})
                  </span>
                </div>
                <div className="receipt-item-row">
                  <span className="receipt-item-label">Student Email:</span>
                  <span className="receipt-item-val">{confirmedEnrollment.studentEmail}</span>
                </div>
                <div className="receipt-item-row">
                  <span className="receipt-item-label">Payment Status:</span>
                  <span className="receipt-item-val" style={{ color: '#059669' }}>Verified &amp; Captured</span>
                </div>

                {confirmedEnrollment.mode === 'offline' ? (
                  <div style={{ marginTop: '14px', padding: '10px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a', fontSize: '0.82rem', color: '#92400e', lineHeight: 1.45 }}>
                    🏢 <strong>Jaipur Classroom Location:</strong><br />
                    P.No. 18, Radha Vihar, Manchwa, Jaipur, Rajasthan 303706.<br />
                    Counseling Helpline: +91 7852034945
                  </div>
                ) : (
                  <div style={{ marginTop: '14px', padding: '10px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', fontSize: '0.82rem', color: '#1e40af', lineHeight: 1.45 }}>
                    🌐 <strong>Live Cohort Access:</strong><br />
                    Login credentials, live class session links and community Discord invites have been queued to {confirmedEnrollment.studentEmail}.
                  </div>
                )}
              </div>

              <div className="success-actions">
                <button
                  type="button"
                  className="btn-print-receipt"
                  onClick={() => window.print()}
                >
                  🖨️ Print Receipt
                </button>
                <button
                  type="button"
                  className="btn-done-receipt"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/my-courses');
                  }}
                >
                  Go to My Courses &rarr;
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default CourseDetail;
